/**
 * Lo que dice el coach en el repaso de correcciones: indicaciones cortas que
 * guían el juego, con su voz (Emmanuel, 16/10: «que el coach te guíe un poco
 * con estas indicaciones pequeñas, y que no haga forzadamente una pregunta que
 * encaje con lo que hay que decir»; sin voz «está muy mudo todo», aunque no
 * sea la primera vez). Son líneas FIJAS: la voz de cada una se genera y se
 * cobra una sola vez (`/speak` con `use: 'review'` la guarda para siempre) y
 * la variante sale del número de la corrección, para que se repitan poco.
 */

const NEXT = [
  'Next one. Say it just like that.',
  "Here's another one. Read it out loud.",
  'Now this one. Say it as it is.',
] as const;
const REACTIONS = ['Nice!', 'Great!', 'Perfect!', 'Well done!'] as const;
const RETRY = ['Almost. Try it once more.', 'Not quite. One more time.'] as const;
const MEMORY = [
  'You know it by heart!',
  'There you go, from memory!',
  "That's it! You've got this one.",
] as const;
/**
 * Salió al 2.º o 3.º intento (card #580, Emmanuel 21/10): no es «de memoria», el
 * coach festeja que siguió intentando.
 */
const EFFORT = [
  'You kept at it, and you got it!',
  'There it is! Practice pays off.',
  'You got there. Nice work sticking with it!',
] as const;

const pick = <T>(list: readonly T[], n: number): T => list[n % list.length]!;

/** De qué es el repaso: correcciones y frases de las charlas, o las palabras guardadas (la Bodega). */
export type ReviewLinesKind = 'talks' | 'words';

/**
 * Al empezar una corrección: la primera abre el repaso; la que vuelve, se pide sin mirar.
 * `savedWord`: la tarjeta es una palabra guardada dentro de su frase (en el Repaso del día van
 * después de las correcciones; sólo cambia la apertura cuando es la primera).
 */
export function openLine(
  index: number,
  returning: boolean,
  kind: ReviewLinesKind = 'talks',
  savedWord = false,
): string {
  if (kind === 'words') {
    return index === 0
      ? "Let's say the words you just saved, each one in a sentence. Say this one just like that."
      : pick(NEXT, index - 1);
  }
  if (index === 0 && savedWord) {
    return returning
      ? "Let's practice a word you saved. This one is back: say it without looking."
      : "Let's practice a word you saved, in a sentence. Say this one just like that.";
  }
  if (index === 0) {
    return returning
      ? "Let's practice a few phrases from your talks. This one is back: say it without looking."
      : "Let's practice a few phrases from your talks. Say this one just like that.";
  }
  return returning ? 'This one is back. Say it without looking.' : pick(NEXT, index - 1);
}

/** Al pasar a sin mirar: una reacción si la dijo bien; si siguió sin decirla, sólo el pedido. */
export function soloLine(index: number, said: boolean): string {
  return said
    ? `${pick(REACTIONS, index)} Now one more time, without looking.`
    : 'Now try it without looking.';
}

/**
 * La dijo sin mirar: el coach lo festeja (encargo 113: quedaba «Now one more
 * time, without looking.» a la vista, como si faltara decirla).
 */
export function memoryLine(index: number): string {
  return pick(MEMORY, index);
}

/** La dijo sin mirar, pero al 2.º o 3.º intento: festeja el esfuerzo, no «de memoria». */
export function effortLine(index: number): string {
  return pick(EFFORT, index);
}

/** Cuando no salió y puede volver a intentar. */
export function retryLine(attempt: number, silence: boolean): string {
  return silence ? "I didn't hear you. Try again." : pick(RETRY, attempt);
}

/** Sin mirar, mal los 3 intentos o «Ver la frase»: vuelve mañana. */
export const AGAIN_TOMORROW_LINE = "No worries. You'll see this one again tomorrow.";
/** Lo mismo con una palabra guardada: no se programa, queda marcada para practicar. */
export const AGAIN_WORD_LINE = "No worries. I marked it as one to practice in your Library.";

export const againLine = (kind: ReviewLinesKind): string =>
  kind === 'words' ? AGAIN_WORD_LINE : AGAIN_TOMORROW_LINE;

/** Al terminar el repaso. */
export const DONE_LINE = "Great work! That's all for now.";

/** Lo que puede decir en una corrección (para preparar sus voces de antemano). */
export function linesFor(
  index: number,
  returning: boolean,
  kind: ReviewLinesKind = 'talks',
  savedWord = false,
): string[] {
  return [
    openLine(index, returning, kind, savedWord),
    ...(returning ? [] : [soloLine(index, true), soloLine(index, false)]),
    retryLine(0, false),
    retryLine(1, false),
    retryLine(0, true),
    memoryLine(index),
    effortLine(index),
    againLine(kind),
  ];
}
